import React, { memo } from 'react';
import { formatCurrency } from '../../utilities/general-utils';

/**
 * Form for entering new loan/credit line details for refinancing
 */
const NewLoanForm = memo(({ newLoanData, setNewLoanData, selectedTotals }) => {
    const handleInputChange = (field, value) => {
        setNewLoanData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const loanTypes = [
        'Personal Loan',
        'Home Equity Line of Credit',
        'Home Equity Loan',
        'Balance Transfer Credit Card',
        'Debt Consolidation Loan',
        'Other'
    ];

    return (
        <div style={{
            backgroundColor: '#fff',
            border: '2px solid #4caf50',
            borderRadius: '12px',
            padding: '2rem',
            marginBottom: '2rem'
        }}>
            <h3 style={{ margin: '0 0 1.5rem 0', color: '#2e7d32' }}>
                New Loan/Credit Line Details
            </h3>
            <p style={{ margin: '0 0 1.5rem 0', color: '#666', fontSize: '0.95rem' }}>
                Enter the details of the new loan or credit line you're considering to pay off the selected accounts.
            </p>

            <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
                gap: '1.5rem'
            }}>
                {/* Loan Type */}
                <div className="formField">
                    <label style={{ 
                        display: 'block', 
                        marginBottom: '0.5rem', 
                        fontWeight: 'bold',
                        color: '#333'
                    }}>
                        Loan Type
                    </label>
                    <select
                        value={newLoanData.loanType}
                        onChange={(e) => handleInputChange('loanType', e.target.value)}
                        className="formField"
                        style={{ width: '100%' }}
                    >
                        {loanTypes.map(type => (
                            <option key={type} value={type}>
                                {type}
                            </option>
                        ))}
                    </select>
                </div>

                {/* Loan Amount */}
                <div className="formField">
                    <label style={{ 
                        display: 'block', 
                        marginBottom: '0.5rem', 
                        fontWeight: 'bold',
                        color: '#333'
                    }}>
                        Loan Amount
                    </label>
                    <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={newLoanData.balance}
                        onChange={(e) => handleInputChange('balance', e.target.value)}
                        className="formField"
                        style={{ width: '100%' }}
                        placeholder={`e.g., ${formatCurrency(selectedTotals.balance)}`}
                    />
                    <small style={{ color: '#666', fontSize: '0.85rem' }}>
                        Suggested: {formatCurrency(selectedTotals.balance)} (total of selected accounts)
                    </small>
                </div>

                {/* APR */}
                <div className="formField">
                    <label style={{ 
                        display: 'block', 
                        marginBottom: '0.5rem', 
                        fontWeight: 'bold',
                        color: '#333'
                    }}>
                        Annual Percentage Rate (APR)
                    </label>
                    <input
                        type="number"
                        step="0.01"
                        min="0"
                        max="50"
                        value={newLoanData.apr}
                        onChange={(e) => handleInputChange('apr', e.target.value)}
                        className="formField"
                        style={{ width: '100%' }}
                        placeholder="e.g., 8.50"
                    />
                    <small style={{ color: '#666', fontSize: '0.85rem' }}>
                        Enter as percentage (e.g., 8.5 for 8.5%)
                    </small>
                </div>

                {/* Monthly Payment */}
                <div className="formField">
                    <label style={{ 
                        display: 'block', 
                        marginBottom: '0.5rem', 
                        fontWeight: 'bold',
                        color: '#333'
                    }}>
                        Monthly Payment
                    </label>
                    <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={newLoanData.monthlyPayment}
                        onChange={(e) => handleInputChange('monthlyPayment', e.target.value)}
                        className="formField"
                        style={{ width: '100%' }}
                        placeholder={`e.g., ${formatCurrency(selectedTotals.monthlyPayment)}`}
                    />
                    <small style={{ color: '#666', fontSize: '0.85rem' }}>
                        Current total: {formatCurrency(selectedTotals.monthlyPayment)}
                    </small>
                </div>

                {/* Term (Optional) */}
                <div className="formField">
                    <label style={{ 
                        display: 'block', 
                        marginBottom: '0.5rem', 
                        fontWeight: 'bold',
                        color: '#333'
                    }}>
                        Loan Term (months) - Optional
                    </label>
                    <input
                        type="number"
                        min="1"
                        max="360"
                        value={newLoanData.termMonths}
                        onChange={(e) => handleInputChange('termMonths', e.target.value)}
                        className="formField"
                        style={{ width: '100%' }}
                        placeholder="e.g., 60"
                    />
                    <small style={{ color: '#666', fontSize: '0.85rem' }}>
                        If provided, payment will be calculated automatically
                    </small>
                </div>
            </div>

            {/* Quick Calculation Info */}
            {newLoanData.balance && newLoanData.apr && newLoanData.monthlyPayment && (
                <div style={{
                    marginTop: '1.5rem',
                    padding: '1rem',
                    backgroundColor: '#f0f7ff',
                    borderRadius: '8px',
                    border: '1px solid #2196f3'
                }}>
                    <h4 style={{ margin: '0 0 0.5rem 0', color: '#1976d2' }}>
                        Quick Calculation
                    </h4>
                    <p style={{ margin: '0', color: '#666', fontSize: '0.9rem' }}>
                        Based on your inputs, this loan will help you consolidate {formatCurrency(selectedTotals.balance)} of debt 
                        with a {parseFloat(newLoanData.apr || 0).toFixed(2)}% APR and {formatCurrency(newLoanData.monthlyPayment)} monthly payment.
                    </p>
                </div>
            )}
        </div>
    );
});

NewLoanForm.displayName = 'NewLoanForm';

export default NewLoanForm;